import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  styled,
  Tooltip,
  Slide,
  Grid,
  Typography,
  Badge,
  Chip,
  Button
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { forwardRef, useState, ReactElement, Ref, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useSelector } from 'react-redux';
import { selectToken, selectUserDetails } from 'src/service/slices/authSlice';
import { fetchEventSource } from '@microsoft/fetch-event-source';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
        
    }
    
    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: 16px 24px;
    border-bottom: 1px solid ${theme.colors.alpha.black[10]};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
`
);

const EmptyNotificationBox = styled(Box)(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    text-align: center;
    height: 250px;
`
);

const NotificationCard = styled(Box)(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-radius: ${theme.shape.borderRadius}px;
    background: ${theme.colors.alpha.white[100]};
    border: 1px solid ${theme.colors.alpha.black[10]};
    box-shadow: 0 2px 5px rgba(0,0,0,0.04);
    margin-bottom: 12px;
    transition: all .2s;
    
    &:hover {
      background: ${theme.colors.alpha.black[5]};
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.06);
    }
`
);

const StyledBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
      font-weight: bold;
      font-size: 10px;
      padding: 0 4px;
      min-width: 18px;
      height: 18px;
      border-radius: 9px;
    }
`
);
const HeaderNotification = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
 
  const handleNotification = async (id: string) => {
   return;
  };

  useEffect(() => {
    if (notifications.length === 0) {
      setOpen(false);
    }
  }, [notifications]);

  return (
    <>
      <Tooltip arrow title="Notificações">
        <IconButton
          color={notifications.length == 0 ? 'secondary' : 'error'}
          onClick={() => {
            setOpen(true);
          }}
        >
          <StyledBadge
            badgeContent={
              notifications.length > 0 ? notifications.length : null
            }
            color="error"
            max={99}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
          >
            <NotificationsActiveIcon />
          </StyledBadge>
        </IconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        scroll="paper"
        onClose={() => setOpen(false)}
        BackdropProps={{ style: { backgroundColor: 'transparent' } }}
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitleWrapper>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationsActiveIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Notificações
            </Typography>
            {notifications.length > 0 && (
              <Chip
                label={notifications.length}
                size="small"
                color="error"
                sx={{ height: 20, fontWeight: 'bold', fontSize: '0.75rem' }}
              />
            )}
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitleWrapper>

        <Box sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
          {notifications.length > 0 ? (
            notifications.map((x) => (
              <NotificationCard key={x.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: 'primary.lighter',
                      borderRadius: '50%',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FolderZipIcon color="primary" />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="medium"
                      color="text.primary"
                    >
                      {`${x.id}.zip`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {x.message || 'Arquivo disponível para download'}
                    </Typography>
                  </Box>
                </Box>
                <a href={x.url} download style={{ textDecoration: 'none' }}>
                  <Tooltip title="Baixar arquivo">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CloudDownloadIcon />}
                      onClick={() => handleNotification(x.id)}
                      sx={{ borderRadius: 2 }}
                    >
                      Baixar
                    </Button>
                  </Tooltip>
                </a>
              </NotificationCard>
            ))
          ) : (
            <EmptyNotificationBox>
              <Box
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: '50%',
                  p: 2,
                  mb: 2,
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <NotificationsActiveIcon
                  sx={{ fontSize: 40, color: 'text.secondary' }}
                />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma notificação
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Você será notificado quando houver novos arquivos disponíveis
              </Typography>
            </EmptyNotificationBox>
          )}
        </Box>
      </DialogWrapper>
    </>
  );
};

export default HeaderNotification;
